<template>
    <div class="flex flex-col gap-2">
        <div class="relative">
            <textarea
                ref="textareaEl"
                v-model="model"
                :rows="rows"
                :placeholder="placeholder"
                :aria-label="placeholder"
                :disabled="isRecording || isTranscribing"
                class="w-full px-3 py-2 pr-14 border-2 border-gray-200 rounded-lg outline-none focus:border-valence-cyan-100 resize-none body-small disabled:bg-gray-50 max-h-[60vh] overflow-y-auto"
                @input="autoResize"
                @blur="$emit('blur')"></textarea>

            <!-- Recording overlay — shown ONLY while actively recording. -->
            <div v-if="isRecording" class="absolute inset-0 rounded-lg bg-purple-50 border-2 border-purple-300 flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-4">
                <span class="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                <span class="body-small text-gray-700">Recording… speak naturally, I'll transcribe.</span>
                <button type="button" class="px-3 py-1 rounded-full bg-purple-600 text-white details hover:bg-purple-700" @click="stopRecording">Stop</button>
            </div>
        </div>

        <!-- Action row: mic + status -->
        <div class="flex items-center gap-2 details">
            <button
                type="button"
                :disabled="isRecording || isTranscribing"
                class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                title="Speak your answer"
                @click="startRecording">
                <i class="bi bi-mic" aria-hidden="true"></i>
                <span>{{ isTranscribing ? "Transcribing…" : "Speak" }}</span>
            </button>

            <span v-if="errorMessage" class="ml-2 text-red-600">{{ errorMessage }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">
import { transcribeRecording } from "/js/transcription.js";
import * as Sentry from "@sentry/browser";
import { useMicConsent } from "~vue/composables/useMicConsent";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

interface Props {
    /** v-model text. */
    modelValue: string;
    rows?: number;
    placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
    rows: 4,
    placeholder: "A moment that comes to mind…",
});

const emit = defineEmits<{
    "update:modelValue": [string];
    "update:busy": [boolean];
    blur: [];
}>();

const model = computed({
    get: () => props.modelValue,
    set: (v: string) => emit("update:modelValue", v),
});

// Grow the textarea to fit its content so long feedback is fully visible
// without an inner scrollbar (capped by max-h in the template) (LAB-497).
const textareaEl = ref<HTMLTextAreaElement | null>(null);

function autoResize() {
    const el = textareaEl.value;
    if (!el) {
        return;
    }
    el.style.height = "auto";
    // scrollHeight excludes the border, but border-box height includes it, so
    // add the border back to avoid clipping the last line.
    const border = el.offsetHeight - el.clientHeight;
    el.style.height = `${el.scrollHeight + border}px`;
}

onMounted(() => {
    // Lock in the rows-based height as the minimum so an empty field keeps its
    // original size, then grow to fit any prefilled content.
    const el = textareaEl.value;
    if (el) {
        el.style.minHeight = `${el.offsetHeight}px`;
    }
    autoResize();
});

// Re-fit after programmatic changes (e.g. appended transcription) once the DOM
// has the new value.
watch(model, () => nextTick(autoResize));

const isRecording = ref(false);
const isTranscribing = ref(false);
const errorMessage = ref<string | null>(null);

// The composer is "busy" whenever audio is being captured or transcribed.
// Parents use this to disable navigation so an in-flight recording can't
// spill over into the next question (LAB-491).
const isBusy = computed(() => isRecording.value || isTranscribing.value);
watch(isBusy, (v) => emit("update:busy", v), { immediate: true });

const { micConsentBlocks } = useMicConsent();

let mediaRecorder: MediaRecorder | null = null;
let recordedChunks: Blob[] = [];
let mediaStream: MediaStream | null = null;

async function startRecording() {
    if (micConsentBlocks(startRecording)) {
        return;
    }

    errorMessage.value = null;
    recordedChunks = [];
    try {
        mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
        errorMessage.value = "Microphone access denied.";
        Sentry.captureException(err);
        return;
    }

    try {
        mediaRecorder = new MediaRecorder(mediaStream);
        mediaRecorder.ondataavailable = (e: BlobEvent) => {
            if (e.data && e.data.size > 0) {
                recordedChunks.push(e.data);
            }
        };
        mediaRecorder.onstop = onRecordingStopped;
        mediaRecorder.start();
        isRecording.value = true;
    } catch (err) {
        errorMessage.value = "Could not start recording.";
        Sentry.captureException(err);
        cleanupStream();
    }
}

function stopRecording() {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
    }
}

async function onRecordingStopped() {
    isRecording.value = false;
    // Capture chunks + mime type BEFORE cleanupStream() — it resets `recordedChunks` to a fresh array.
    const chunks = recordedChunks;
    const mimeType = mediaRecorder?.mimeType || "audio/webm";
    cleanupStream();
    if (chunks.length === 0) {
        return;
    }

    const blob = new Blob(chunks, { type: mimeType });
    isTranscribing.value = true;
    try {
        const transcript = await transcribeRecording({
            blob,
            onError: (err: unknown) => Sentry.captureException(err),
        });
        if (typeof transcript === "string" && transcript.trim().length > 0) {
            // Append to existing text; on a fresh field, just set it.
            const existing = model.value.trim();
            model.value = existing ? existing + " " + transcript : transcript;
        } else {
            errorMessage.value = "Couldn't transcribe — try again.";
        }
    } catch (err) {
        Sentry.captureException(err);
        errorMessage.value = "Couldn't transcribe — try again.";
    } finally {
        isTranscribing.value = false;
    }
}

function cleanupStream() {
    mediaStream?.getTracks().forEach((track) => track.stop());
    mediaStream = null;
    mediaRecorder = null;
    recordedChunks = [];
}

// Release the mic if the composer is torn down mid-recording (e.g. parent
// page navigates away). We don't try to transcribe a partial recording here.
onBeforeUnmount(() => {
    if (mediaRecorder && mediaRecorder.state !== "inactive") {
        try {
            mediaRecorder.onstop = null;
            mediaRecorder.stop();
        } catch (err) {
            Sentry.captureException(err);
        }
    }
    cleanupStream();
});
</script>
